import { ExportExclusionsPanelController } from '../controller/ExportExclusionsPanelController';
import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { TemplateState } from '../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../ui/gzInput/component/GzInputWebComponent';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzTableWebComponent } from '../../../ui/gzTable';
import { GzTextWebComponent } from '../../../ui/gzText';
import { RegexValidator } from '../../../ui/gzForm/validators/regex.validator';
import { translateFn } from '../../../utils/translator.helper';

export class ExportExclusionsView extends BaseWebComponentView {
  public controller!: ExportExclusionsPanelController;

  public dom: {
    $wrapper: HTMLElement;

    $infoMessage: GzTextWebComponent;

    $columnMappingTitle: GzTitleWebComponent;
    $dataPreviewTitle: GzTitleWebComponent;

    $objectTypeFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $excludedItemFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $exclusionModulesFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $exclusionDataPreview: GzTableWebComponent;

    $saveButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.export-wrapper'),
      $infoMessage: this.shadow.querySelector('#export-exclusions-message'),
      $columnMappingTitle: this.shadow.querySelector('#export-column-mapping-title'),
      $dataPreviewTitle: this.shadow.querySelector('#export-data-preview-title'),
      $objectTypeFormField: this.shadow.querySelector('#export-object-type-form-field'),
      $excludedItemFormField: this.shadow.querySelector('#export-excluded-item-form-field'),
      $exclusionModulesFormField: this.shadow.querySelector('#export-exclusion-modules-form-field'),
      $exclusionDataPreview: this.shadow.querySelector('#export-exclusions-data-preview') as GzTableWebComponent,

      $saveButton: this.shadow.querySelector('#save-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$infoMessage.innerText = translateFn('This page only displays the first five selected exclusions. The CSV file will include the entire selection.');

    this.dom.$columnMappingTitle.innerText = translateFn('Column Data Mapping');
    this.dom.$dataPreviewTitle.innerText = translateFn('Data preview');

    this.dom.$objectTypeFormField.label = translateFn('Object type');
    this.dom.$excludedItemFormField.label = translateFn('Excluded item');
    this.dom.$exclusionModulesFormField.label = translateFn('Modules');

    this.dom.$saveButton.innerHTML = this.commonStrings.confirm;
    this.dom.$cancelButton.innerHTML = this.commonStrings.cancel;
  }

  buildSuccessView(viewData: any) {
    this.setValidators();
    this.renderDataPreview(viewData);
    this.removeLoadingState();
  }

  public getDefaultTableHeader() {
    return [
      translateFn('Object type'),
      translateFn('Excluded item'),
      translateFn('Modules'),
    ];
  }

  private setValidators() {
    const csvHeaderValidator = [
      RegexValidator(
        /^[\p{L}0-9]+([\s@#%&'*+\-.\p{L}0-9])*$/u,
        translateFn(
          'CSV column header can contain only alphanumeric characters and the following symbols: {allowedCharacters}',
          {
            allowedCharacters: "@#%&'*+.",
            interpolation: {
              escapeValue: false,
            },
          }
        )
      ),
    ];

    this.dom.$objectTypeFormField.Validators = csvHeaderValidator;
    this.dom.$excludedItemFormField.Validators = csvHeaderValidator;
    this.dom.$exclusionModulesFormField.Validators = csvHeaderValidator;
  }

  renderDataPreview(viewData) {
    const tableHeader = {
      header: this.getDefaultTableHeader(),
    };

    // We must only show in data preview the first 5 entries
    const tableData = viewData.exclusions.slice(0, 5).map(exclusion => {
      return {
        value: [exclusion.objectType, exclusion.excludedItems, exclusion.modules.toString()],
      };
    });

    this.dom.$exclusionDataPreview.head = tableHeader;
    this.dom.$exclusionDataPreview.data = tableData;
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.removeLoadingState();

    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }
}
